import { CHARACTER_ACTIONS } from "@/character/actions";
import {
  defaultGridTuning,
  gridTuningForImageSize,
  allRowsSplit,
  emptyRowOffsetX,
  emptyRowOffsetY,
  emptyRowSplit,
  fullRowSplit,
  normalizeGridTuning,
  type GridTuning,
} from "@/character/draft";
import {
  describeSheetLayout,
  fitSheetToMaxWidth,
  resolveSheetLayout,
  rowSplitOffsetY,
  sheetLayoutFromImage,
  sheetLayoutFromImageLenient,
  sheetToDisplayY,
  supportsTemplateGridLayout,
  templateScaleFactor,
} from "@/character/grid";
import {
  applySheetUploadClean,
  bindSheetUploadCleanControls,
  readSheetUploadCleanPrefsFromHost,
  renderSheetUploadCleanControlsHtml,
} from "@/character/sheet-upload-clean";
import {
  drawGridSheet,
  drawRowStrip,
  startRowLoop,
} from "@/character/sheet-preview-canvas";

const SHEET_MAX_WIDTH = 640;

export type SheetUploadPreviewOptions = {
  /** Raw upload before clean pass (for re-apply). */
  sourceDataUrl?: string;
  onSheetDataUrlChange?: (url: string) => void;
  /**
   * Lock template pitch, drag grid on canvas, view zoom only (no free cell size).
   */
  templateAlignMode?: boolean;
  /** D-pad + icon actions (character create modal). */
  compactToolbar?: boolean;
  /** Omit clean controls (handled elsewhere on the page). */
  hideCleanControls?: boolean;
};

export function mountSheetUploadPreview(
  host: HTMLElement,
  dataUrl: string,
  tuning: GridTuning,
  onTuningChange: (tuning: GridTuning) => void,
  options?: SheetUploadPreviewOptions,
): () => void {
  let cleanups: Array<() => void> = [];
  let unbindClean: (() => void) | undefined;
  let currentTuning = normalizeGridTuning(tuning);
  let zoomScale = 1;
  let sourceDataUrl = options?.sourceDataUrl ?? dataUrl;
  let activeDataUrl = dataUrl;
  const templateAlignMode = options?.templateAlignMode === true;
  const compactToolbar = options?.compactToolbar === true;
  const hideCleanControls = options?.hideCleanControls === true;
  const enableCanvasGridDrag = true;
  const expandedLoopRows = new Set<number>();
  let activeAdjustRow: number | null = null;

  if (templateAlignMode) {
    currentTuning = {
      ...currentTuning,
      lockTemplateGrid: true,
      frameSize: null,
    };
  }

  const alignToolsHtml = templateAlignMode
    ? `
      <div class="grid-align-tools grid-align-tools--drag">
        <p class="hint">ลากบนภาพเพื่อเลื่อนกริดให้ทับแถว action (IDLE / RUN / …) · ซูมด้านล่างเพื่อดูรายละเอียด</p>
        <div class="grid-align-controls grid-align-controls--compact">
          <button type="button" class="btn btn-ghost" id="align-reset">รีเซ็ตตำแหน่งกริด</button>
        </div>
      </div>`
    : compactToolbar
      ? ""
      : `
      <div class="grid-align-tools">
        <p class="hint">ปรับกริดให้ตรงเส้นในภาพ</p>
        <label class="grid-lock-template">
          <input type="checkbox" id="lock-template-grid" />
          <span id="lock-template-label">โครงเทมเพลต (ป้าย + 6 เฟรม) · pitch ตามภาพ</span>
        </label>
        <div class="grid-align-controls">
          <div class="grid-align-axis">
            <span class="grid-align-label">เซลล์</span>
            <input
              type="number"
              class="text-input grid-align-input grid-frame-size-input"
              id="frame-size"
              min="16"
              max="512"
              step="1"
              placeholder="อัตโนมัติ"
            />
            <span class="hint grid-frame-size-hint">px (ว่าง = อ่านจากภาพ)</span>
          </div>
          <div class="grid-align-axis">
            <span class="grid-align-label">X</span>
            <button type="button" class="btn btn-ghost btn-nudge" data-axis="x" data-delta="-10">−10</button>
            <button type="button" class="btn btn-ghost btn-nudge" data-axis="x" data-delta="-1">←</button>
            <input type="number" class="text-input grid-align-input" id="align-x" value="0" />
            <button type="button" class="btn btn-ghost btn-nudge" data-axis="x" data-delta="1">→</button>
            <button type="button" class="btn btn-ghost btn-nudge" data-axis="x" data-delta="10">+10</button>
          </div>
          <div class="grid-align-axis">
            <span class="grid-align-label">Y</span>
            <button type="button" class="btn btn-ghost btn-nudge" data-axis="y" data-delta="-10">−10</button>
            <button type="button" class="btn btn-ghost btn-nudge" data-axis="y" data-delta="-1">↑</button>
            <input type="number" class="text-input grid-align-input" id="align-y" value="0" />
            <button type="button" class="btn btn-ghost btn-nudge" data-axis="y" data-delta="1">↓</button>
            <button type="button" class="btn btn-ghost btn-nudge" data-axis="y" data-delta="10">+10</button>
          </div>
          <button type="button" class="btn btn-ghost" id="infer-pitch">ใช้ pitch จากภาพ</button>
          <button type="button" class="btn btn-ghost" id="align-reset">รีเซ็ตการปรับกริด</button>
        </div>
        ${
          hideCleanControls
            ? ""
            : `<div class="sheet-upload-clean-wrap">
          ${renderSheetUploadCleanControlsHtml("preview-clean")}
          <button type="button" class="btn btn-ghost btn-sm" id="apply-sheet-clean">ใช้การลบกรอบกับภาพ</button>
        </div>`
        }
      </div>`;

  const zoomBarHtml = compactToolbar
    ? ""
    : `
      <div class="grid-sheet-zoom-bar">
        <span class="hint">ซูมภาพ</span>
        <button type="button" class="btn btn-ghost btn-zoom" data-zoom="1">100%</button>
        <button type="button" class="btn btn-ghost btn-zoom" data-zoom="2">200%</button>
        <button type="button" class="btn btn-ghost btn-zoom" data-zoom="3">300%</button>
      </div>`;

  const compactToolbarHtml = `
        <nav class="grid-sheet-toolbar grid-sheet-toolbar--game" aria-label="เครื่องมือกริด">
          <div class="grid-sheet-toolbar-group" role="group" aria-label="ปรับกริด">
            <button type="button" class="grid-icon-btn grid-sheet-tool-btn" id="infer-pitch" title="ใช้ pitch จากภาพ" aria-label="ใช้ pitch จากภาพ">◎</button>
            <button type="button" class="grid-icon-btn grid-sheet-tool-btn" id="align-reset" title="รีเซ็ตกริด" aria-label="รีเซ็ตการปรับกริด">↺</button>
            <button
              type="button"
              class="grid-icon-btn grid-sheet-tool-btn grid-sheet-split-all-btn"
              id="split-all-rows"
              title="แยกทุกแถวกริด · ลากปรับ Y แถวละแถว"
              aria-label="แยกทุกแถวกริด"
              aria-pressed="false"
            >แยก</button>
          </div>
          <span class="grid-sheet-toolbar-divider" aria-hidden="true"></span>
          <div class="grid-sheet-toolbar-segment" role="group" aria-label="ซูมภาพ">
            <button type="button" class="grid-icon-btn btn-zoom is-active" data-zoom="1" aria-label="ซูม 100%">1×</button>
            <button type="button" class="grid-icon-btn btn-zoom" data-zoom="2" aria-label="ซูม 200%">2×</button>
            <button type="button" class="grid-icon-btn btn-zoom" data-zoom="3" aria-label="ซูม 300%">3×</button>
          </div>
          <div class="grid-sheet-toolbar-info">
            <button
              type="button"
              class="grid-icon-btn grid-info-btn"
              id="sheet-grid-info-btn"
              aria-label="รายละเอียดภาพและคำเตือน"
              aria-expanded="false"
              aria-controls="sheet-grid-info-popover"
            >i</button>
            <div id="sheet-grid-info-popover" class="grid-info-popover" role="dialog" hidden>
              <p class="hint grid-panel-meta">กำลังโหลด…</p>
              <ul class="grid-panel-warnings" hidden></ul>
            </div>
          </div>
        </nav>`;

  const panelHeadHtml = compactToolbar
    ? `
      <div class="grid-panel-head grid-panel-head--compact">
        <h2 id="upload-preview-title" class="grid-panel-title">ตัวอย่างกริดและแอนิเมชัน</h2>
        ${compactToolbarHtml}
      </div>`
    : `
      <div class="grid-panel-head">
        <h2 id="upload-preview-title" class="grid-panel-title">ตัวอย่างกริดและแอนิเมชัน</h2>
        <p class="hint grid-panel-meta">กำลังโหลด…</p>
        <ul class="grid-panel-warnings" hidden></ul>
      </div>`;

  host.innerHTML = `
    <section class="character-grid-panel sheet-upload-preview${compactToolbar ? " sheet-upload-preview--compact" : ""}" aria-labelledby="upload-preview-title">
      ${panelHeadHtml}
      ${alignToolsHtml}
      ${zoomBarHtml}
      <div class="grid-sheet-zoom-wrap">
        <canvas
          class="grid-sheet-canvas${enableCanvasGridDrag ? " grid-sheet-canvas--drag" : ""}"
          aria-label="${enableCanvasGridDrag ? "ลากเพื่อเลื่อนกริดบนสไปรต์ชีต" : "ภาพสไปรต์ชีตพร้อมเส้นกริด"}"
        ></canvas>
      </div>
      <div class="grid-loop-stage">
        <div class="grid-loop-list"></div>
        <div class="grid-loop-frames-dock" hidden></div>
      </div>
    </section>
  `;

  const metaEl = host.querySelector<HTMLElement>(".grid-panel-meta");
  const warningsEl = host.querySelector<HTMLElement>(".grid-panel-warnings");
  const sheetCanvas = host.querySelector<HTMLCanvasElement>(".grid-sheet-canvas");
  const loopList = host.querySelector<HTMLElement>(".grid-loop-list");
  const framesDock = host.querySelector<HTMLElement>(".grid-loop-frames-dock");
  const zoomWrap = host.querySelector<HTMLElement>(".grid-sheet-zoom-wrap");
  const previewRoot = host.querySelector<HTMLElement>(".sheet-upload-preview");
  const inputX = host.querySelector<HTMLInputElement>("#align-x");
  const inputY = host.querySelector<HTMLInputElement>("#align-y");
  const inputFrameSize = host.querySelector<HTMLInputElement>("#frame-size");
  const lockTemplate = host.querySelector<HTMLInputElement>("#lock-template-grid");
  const lockTemplateLabel = host.querySelector<HTMLElement>("#lock-template-label");
  const infoBtn = host.querySelector<HTMLButtonElement>("#sheet-grid-info-btn");
  const infoPopover = host.querySelector<HTMLElement>("#sheet-grid-info-popover");
  let removeInfoPopoverDocListener: (() => void) | undefined;

  const img = new Image();
  img.decoding = "async";

  const stopLoops = () => {
    for (const stop of cleanups) stop();
    cleanups = [];
  };

  const syncInputs = () => {
    if (inputX) inputX.value = String(currentTuning.offset.x);
    if (inputY) inputY.value = String(currentTuning.offset.y);
    if (lockTemplate) lockTemplate.checked = currentTuning.lockTemplateGrid;
    if (inputFrameSize) {
      inputFrameSize.value =
        currentTuning.frameSize != null ? String(currentTuning.frameSize) : "";
      inputFrameSize.disabled = currentTuning.lockTemplateGrid;
    }
    const splitAllBtn = host.querySelector<HTMLButtonElement>("#split-all-rows");
    if (splitAllBtn) {
      const on = allRowsSplit(currentTuning);
      splitAllBtn.classList.toggle("is-active", on);
      splitAllBtn.setAttribute("aria-pressed", on ? "true" : "false");
    }
  };

  const scrollActiveRowIntoView = () => {
    if (!zoomWrap || !sheetCanvas || !img.naturalWidth || activeAdjustRow == null) return;
    if (!allRowsSplit(currentTuning)) return;

    const layout = resolveSheetLayout(img, currentTuning);
    const { displayHeight } = fitSheetToMaxWidth(
      layout.sheetWidth,
      layout.sheetHeight,
      SHEET_MAX_WIDTH,
    );
    const grid = layout.grid;
    const row = activeAdjustRow;
    const topSheetY = grid.alignY + row * grid.frameHeight + rowSplitOffsetY(currentTuning, row);
    const bottomSheetY = topSheetY + grid.frameHeight;

    const y0 = sheetToDisplayY(topSheetY, layout.sheetHeight, displayHeight) * zoomScale;
    const y1 = sheetToDisplayY(bottomSheetY, layout.sheetHeight, displayHeight) * zoomScale;
    const bandCenter = (y0 + y1) / 2;
    const target = bandCenter - zoomWrap.clientHeight / 2;
    const maxScroll = Math.max(0, zoomWrap.scrollHeight - zoomWrap.clientHeight);
    zoomWrap.scrollTop = Math.max(0, Math.min(target, maxScroll));
  };

  const applyCanvasZoom = () => {
    if (!sheetCanvas) return;
    const baseW = sheetCanvas.width;
    const baseH = sheetCanvas.height;
    if (baseW > 0) {
      sheetCanvas.style.width = `${Math.round(baseW * zoomScale)}px`;
      sheetCanvas.style.maxWidth = "none";
    }
    if (baseH > 0) {
      sheetCanvas.style.height = `${Math.round(baseH * zoomScale)}px`;
    }
    requestAnimationFrame(() => scrollActiveRowIntoView());
  };

  const setTuning = (next: GridTuning) => {
    currentTuning = normalizeGridTuning(next);
    syncInputs();
    onTuningChange(currentTuning);
    paint();
  };

  const paint = () => {
    if (!img.naturalWidth) return;
    stopLoops();

    const layout = resolveSheetLayout(img, currentTuning);
    const { headline, warnings } = describeSheetLayout(layout);
    const scale = templateScaleFactor(layout.sheetWidth, layout.sheetHeight);
    const scaleNote =
      scale != null && scale > 1 && Math.abs(scale - Math.round(scale)) < 0.001
        ? ` · สเกลเทมเพลต ${Math.round(scale)}×`
        : "";

    if (lockTemplateLabel) {
      const cell = layout.grid.frameWidth;
      lockTemplateLabel.textContent = compactToolbar
        ? `เทมเพลต · ${cell}px`
        : `โครงเทมเพลต (ป้าย + 6 เฟรม) · pitch ${cell}×${layout.grid.frameHeight} px จากภาพ`;
    }

    if (metaEl) {
      metaEl.textContent = `${headline}${scaleNote} · offset X ${currentTuning.offset.x} Y ${currentTuning.offset.y}`;
    }

    if (warningsEl) {
      if (warnings.length === 0) {
        warningsEl.hidden = true;
        warningsEl.replaceChildren();
      } else {
        warningsEl.hidden = false;
        warningsEl.replaceChildren(
          ...warnings.map((text) => {
            const li = document.createElement("li");
            li.textContent = text;
            return li;
          }),
        );
      }
    }

    if (infoBtn) {
      infoBtn.classList.toggle("grid-info-btn--warn", warnings.length > 0);
    }

    if (previewRoot) {
      previewRoot.classList.toggle("sheet-upload-preview--split-rows", allRowsSplit(currentTuning));
    }

    if (sheetCanvas) {
      drawGridSheet(
        sheetCanvas,
        img,
        layout,
        SHEET_MAX_WIDTH,
        currentTuning,
        allRowsSplit(currentTuning) ? activeAdjustRow : null,
      );
      applyCanvasZoom();
    }

    if (loopList) {
      loopList.replaceChildren();
      for (const action of CHARACTER_ACTIONS) {
        const rowEl = document.createElement("article");
        const showFrames = expandedLoopRows.has(action.row);
        const rowsSplitMode = allRowsSplit(currentTuning);
        const isActiveAdjust = rowsSplitMode && activeAdjustRow === action.row;
        if (compactToolbar) {
          rowEl.className = `grid-loop-row grid-loop-row--compact-toggle${rowsSplitMode ? " grid-loop-row--split" : ""}${isActiveAdjust ? " grid-loop-row--active-adjust" : ""}${showFrames ? " grid-loop-row--frames-open" : ""}`;
          rowEl.dataset.actionRow = String(action.row);
          rowEl.innerHTML = `
            <span class="grid-loop-label">${action.templateLabel}</span>
            <button
              type="button"
              class="grid-loop-player-toggle"
              aria-pressed="${isActiveAdjust ? "true" : "false"}"
              aria-label="${rowsSplitMode ? `${action.label} · แตะเลือกเพื่อปรับกริดแถวนี้` : `${action.label} · แตะเพื่อ${showFrames ? "ซ่อน" : "แสดง"}เฟรม`}"
            >
              <canvas class="grid-loop-player" role="img" aria-hidden="true"></canvas>
            </button>
            ${
              rowsSplitMode
                ? `<button type="button" class="grid-loop-frames-toggle" aria-expanded="${showFrames ? "true" : "false"}">เฟรม</button>`
                : ""
            }
          `;
        } else {
          rowEl.className = "grid-loop-row";
          rowEl.innerHTML = `
            <span class="grid-loop-label">${action.templateLabel}</span>
            <div class="grid-loop-body">
              <canvas class="grid-row-strip" aria-hidden="true"></canvas>
              <canvas class="grid-loop-player" role="img" aria-label="${action.label} แอนิเมชัน loop"></canvas>
            </div>
          `;
        }
        loopList.append(rowEl);

        const player = rowEl.querySelector<HTMLCanvasElement>(".grid-loop-player");
        if (player) {
          cleanups.push(
            startRowLoop(player, img, layout, action.row, undefined, "loop", undefined, currentTuning),
          );
        }
      }

      if (compactToolbar && framesDock) {
        framesDock.replaceChildren();
        const expandedActions = CHARACTER_ACTIONS.filter((action) =>
          expandedLoopRows.has(action.row),
        );
        framesDock.hidden = expandedActions.length === 0;
        for (const action of expandedActions) {
          const block = document.createElement("div");
          block.className = "grid-loop-frames-block";
          block.dataset.actionRow = String(action.row);
          block.innerHTML = `
            <span class="grid-loop-frames-block__label">${action.templateLabel}</span>
            <div class="grid-loop-frames grid-loop-frames--dock">
              <canvas class="grid-row-strip" aria-label="${action.label} เฟรม 1–6"></canvas>
            </div>
          `;
          framesDock.append(block);
          const strip = block.querySelector<HTMLCanvasElement>(".grid-row-strip");
          if (strip) {
            const stripCell = Math.round(44 * zoomScale);
            drawRowStrip(strip, img, layout, action.row, currentTuning, stripCell);
          }
        }
      }
    }

    if (allRowsSplit(currentTuning) && activeAdjustRow != null) {
      requestAnimationFrame(() => scrollActiveRowIntoView());
    }
  };

  const bindGridDrag = () => {
    if (!enableCanvasGridDrag || !sheetCanvas) return;
    if (sheetCanvas.dataset.gridDragBound === "1") return;
    sheetCanvas.dataset.gridDragBound = "1";

    let dragging = false;
    let lastX = 0;
    let lastY = 0;

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      dragging = true;
      lastX = event.clientX;
      lastY = event.clientY;
      sheetCanvas.setPointerCapture(event.pointerId);
      sheetCanvas.classList.add("grid-sheet-canvas--grabbing");
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!dragging || !img.naturalWidth) return;
      const rect = sheetCanvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      lastX = event.clientX;
      lastY = event.clientY;
      const layout = resolveSheetLayout(img, currentTuning);
      const sheetDx = (dx / rect.width) * layout.sheetWidth;
      const sheetDy = (dy / rect.height) * layout.sheetHeight;
      const base = normalizeGridTuning(currentTuning);
      if (allRowsSplit(base)) {
        const row =
          activeAdjustRow != null && activeAdjustRow >= 0 && activeAdjustRow < base.rowSplit.length
            ? activeAdjustRow
            : null;
        if (row == null) return;
        const rowOffsetY = [...base.rowOffsetY];
        const rowOffsetX = [...base.rowOffsetX];
        rowOffsetY[row] += Math.round(sheetDy);
        rowOffsetX[row] += Math.round(sheetDx);
        setTuning({
          ...base,
          lockTemplateGrid: true,
          frameSize: null,
          offset: { ...base.offset },
          rowOffsetY,
          rowOffsetX,
        });
      } else {
        setTuning({
          ...base,
          lockTemplateGrid: true,
          frameSize: null,
          offset: {
            x: base.offset.x + Math.round(sheetDx),
            y: base.offset.y + Math.round(sheetDy),
          },
        });
      }
    };

    const endDrag = (event: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      sheetCanvas.classList.remove("grid-sheet-canvas--grabbing");
      if (sheetCanvas.hasPointerCapture(event.pointerId)) {
        sheetCanvas.releasePointerCapture(event.pointerId);
      }
    };

    sheetCanvas.addEventListener("pointerdown", onPointerDown);
    sheetCanvas.addEventListener("pointermove", onPointerMove);
    sheetCanvas.addEventListener("pointerup", endDrag);
    sheetCanvas.addEventListener("pointercancel", endDrag);
  };

  const bindControls = () => {
    host.querySelectorAll<HTMLButtonElement>(".btn-nudge").forEach((button) => {
      button.addEventListener("click", () => {
        const axis = button.dataset.axis;
        const delta = Number(button.dataset.delta ?? 0);
        if (axis === "x") {
          setTuning({
            ...currentTuning,
            offset: { ...currentTuning.offset, x: currentTuning.offset.x + delta },
          });
        }
        if (axis === "y") {
          setTuning({
            ...currentTuning,
            offset: { ...currentTuning.offset, y: currentTuning.offset.y + delta },
          });
        }
      });
    });

    host.querySelector("#infer-pitch")?.addEventListener("click", () => {
      if (!img.naturalWidth) return;
      setTuning(
        gridTuningForImageSize(
          img.naturalWidth,
          img.naturalHeight,
          supportsTemplateGridLayout,
        ),
      );
    });

    host.querySelector("#split-all-rows")?.addEventListener("click", () => {
      const base = normalizeGridTuning(currentTuning);
      if (allRowsSplit(base)) {
        activeAdjustRow = null;
        setTuning({
          ...base,
          rowSplit: emptyRowSplit(),
          rowOffsetY: emptyRowOffsetY(),
          rowOffsetX: emptyRowOffsetX(),
        });
      } else {
        activeAdjustRow = 0;
        setTuning({
          ...base,
          rowSplit: fullRowSplit(),
        });
      }
    });

    host.querySelector("#align-reset")?.addEventListener("click", () => {
      activeAdjustRow = null;
      if (img.naturalWidth) {
        setTuning(
          gridTuningForImageSize(
            img.naturalWidth,
            img.naturalHeight,
            supportsTemplateGridLayout,
          ),
        );
      } else {
        setTuning(defaultGridTuning());
      }
    });

    inputX?.addEventListener("change", () => {
      setTuning({
        ...currentTuning,
        offset: { ...currentTuning.offset, x: Number(inputX.value) || 0 },
      });
    });
    inputY?.addEventListener("change", () => {
      setTuning({
        ...currentTuning,
        offset: { ...currentTuning.offset, y: Number(inputY.value) || 0 },
      });
    });

    lockTemplate?.addEventListener("change", () => {
      setTuning({
        ...currentTuning,
        lockTemplateGrid: lockTemplate.checked,
        frameSize: lockTemplate.checked ? null : currentTuning.frameSize,
      });
    });

    inputFrameSize?.addEventListener("change", () => {
      const raw = inputFrameSize.value.trim();
      const frameSize = raw === "" ? null : Number(raw);
      setTuning({
        ...currentTuning,
        lockTemplateGrid: false,
        frameSize:
          frameSize != null && Number.isFinite(frameSize) ? Math.round(frameSize) : null,
      });
    });

    host.querySelectorAll<HTMLButtonElement>(".btn-zoom").forEach((button) => {
      button.addEventListener("click", () => {
        zoomScale = Number(button.dataset.zoom ?? 1);
        host.querySelectorAll(".btn-zoom").forEach((el) => el.classList.remove("is-active"));
        button.classList.add("is-active");
        applyCanvasZoom();
      });
    });

    const cleanWrap = host.querySelector<HTMLElement>(".sheet-upload-clean-wrap");
    if (cleanWrap) {
      unbindClean?.();
      unbindClean = bindSheetUploadCleanControls(cleanWrap);
    }

    host.querySelector("#apply-sheet-clean")?.addEventListener("click", () => {
      void (async () => {
        const prefs = readSheetUploadCleanPrefsFromHost(host);
        try {
          activeDataUrl = await applySheetUploadClean(sourceDataUrl, prefs);
          img.src = activeDataUrl;
          options?.onSheetDataUrlChange?.(activeDataUrl);
        } catch {
          if (metaEl) metaEl.textContent = "ลบกรอบเทมเพลตไม่สำเร็จ";
        }
      })();
    });

    bindGridDrag();

    if (compactToolbar && loopList && loopList.dataset.loopToggleBound !== "1") {
      loopList.dataset.loopToggleBound = "1";
      loopList.addEventListener("click", (event) => {
        const framesBtn = (event.target as HTMLElement).closest<HTMLButtonElement>(
          ".grid-loop-frames-toggle",
        );
        if (framesBtn) {
          const rowEl = framesBtn.closest<HTMLElement>(".grid-loop-row");
          const row = Number(rowEl?.dataset.actionRow);
          if (!rowEl || !Number.isFinite(row)) return;
          if (allRowsSplit(currentTuning)) activeAdjustRow = row;
          if (expandedLoopRows.has(row)) expandedLoopRows.delete(row);
          else expandedLoopRows.add(row);
          paint();
          return;
        }

        const toggle = (event.target as HTMLElement).closest<HTMLButtonElement>(
          ".grid-loop-player-toggle",
        );
        if (!toggle) return;
        const rowEl = toggle.closest<HTMLElement>(".grid-loop-row");
        const row = Number(rowEl?.dataset.actionRow);
        if (!rowEl || !Number.isFinite(row)) return;

        if (allRowsSplit(currentTuning)) {
          activeAdjustRow = row;
          paint();
          return;
        }

        if (expandedLoopRows.has(row)) expandedLoopRows.delete(row);
        else expandedLoopRows.add(row);
        paint();
      });
    }

    removeInfoPopoverDocListener?.();
    removeInfoPopoverDocListener = undefined;

    if (infoBtn && infoPopover) {
      const closeInfoPopover = () => {
        infoPopover.hidden = true;
        infoBtn.setAttribute("aria-expanded", "false");
      };

      infoBtn.addEventListener("click", (event) => {
        event.stopPropagation();
        const open = infoPopover.hidden;
        infoPopover.hidden = !open;
        infoBtn.setAttribute("aria-expanded", open ? "true" : "false");
      });

      const onDocClick = (event: MouseEvent) => {
        if (infoPopover.hidden) return;
        const target = event.target;
        if (!(target instanceof Node)) return;
        if (infoPopover.contains(target) || infoBtn.contains(target)) return;
        closeInfoPopover();
      };
      document.addEventListener("click", onDocClick);
      removeInfoPopoverDocListener = () => {
        document.removeEventListener("click", onDocClick);
      };
    }
  };

  img.onload = () => {
    try {
      if (templateAlignMode) {
        sheetLayoutFromImageLenient(img);
      } else {
        sheetLayoutFromImage(img);
      }
      syncInputs();
      bindControls();
      paint();
    } catch (err) {
      if (metaEl) {
        metaEl.textContent =
          err instanceof Error ? err.message : "ขนาดภาพไม่ตรงกริดที่รองรับ";
      }
    }
  };
  img.onerror = () => {
    if (metaEl) metaEl.textContent = "โหลดภาพไม่สำเร็จ";
  };
  img.src = activeDataUrl;

  return () => {
    removeInfoPopoverDocListener?.();
    removeInfoPopoverDocListener = undefined;
    unbindClean?.();
    stopLoops();
    host.replaceChildren();
  };
}
